import { isThemeName } from 'lib/colors'
import type { ThemeName } from 'lib/colors'
import { THEME_STORAGE_KEY, DARK_SCHEME } from 'server/lib/inline-scripts'

export type ThemePreference = ThemeName | 'system'

const isThemePreference = (value: string): value is ThemePreference => value === 'system' || isThemeName(value)

let current: ThemePreference = 'light'

export const getThemePreference = (): ThemePreference => current

const readStoredPreference = (): ThemePreference => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    return stored !== null && isThemePreference(stored) ? stored : 'light'
  } catch {
    return 'light'
  }
}

// Light is the default, so choosing it forgets the stored preference.
const storePreference = (preference: ThemePreference): void => {
  try {
    if (preference === 'light') window.localStorage.removeItem(THEME_STORAGE_KEY)
    else window.localStorage.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    // Storage blocked: the choice lasts until the page unloads.
  }
}

const applyPreference = (preference: ThemePreference): void => {
  current = preference
  const theme = preference === 'system' ? (window.matchMedia?.(DARK_SCHEME).matches ? 'dark' : 'light') : preference
  if (theme === 'light') delete document.documentElement.dataset.theme
  else document.documentElement.dataset.theme = theme
}

export const setThemePreference = (preference: ThemePreference): void => {
  storePreference(preference)
  applyPreference(preference)
}

// ?theme=dark (or system) sticks across visits and ?theme=light clears it.
export const applyThemeOptIn = (): void => {
  const requested = new URLSearchParams(window.location.search).get('theme')
  if (requested !== null && isThemePreference(requested)) storePreference(requested)
  applyPreference(readStoredPreference())
  window.matchMedia?.(DARK_SCHEME).addEventListener('change', () => {
    if (current === 'system') applyPreference('system')
  })
}
